"use client";

import { useTranslations } from "next-intl";
import { useEffect } from "react";
import { toast } from "sonner";

const DEFAULT_THRESHOLD_MS = 20_000;

// Shared toast id so repeated hints don't stack while the same tx is pending.
// Different transaction flows can override via `toastId` if they need a
// dedicated hint.
const DEFAULT_TOAST_ID = "tx-long-hint";

export interface UseLongTxHintOptions {
  /** Milliseconds to wait while pending before showing the hint. Default 20s. */
  thresholdMs?: number;
  /** Override the localized hint copy. */
  message?: string;
  /** Sonner toast id; share to dedupe, override to run hints in parallel. */
  toastId?: string;
}

/**
 * Shows a Persian long-transaction hint (sonner toast.info) after {@link UseLongTxHintOptions.thresholdMs}
 * of continuous `isPending`. Cancels the pending timer when `isPending` flips false or on unmount,
 * and dismisses the toast in the same cleanup path so a resolved tx doesn't leave stale copy on screen.
 *
 * Use from any transaction-initiating hook (stake, claim, withdraw, approve, convert,
 * fast-checkout, buy NFT, etc.) to surface the shared "network busy, don't refresh" message.
 * Keeping the implementation toast-based (instead of component-rendered) means this hook is
 * a drop-in for any flow without layout changes.
 */
export function useLongTxHint(
  isPending: boolean,
  opts?: UseLongTxHintOptions,
): void {
  const t = useTranslations("errors");
  const thresholdMs = opts?.thresholdMs ?? DEFAULT_THRESHOLD_MS;
  const message = opts?.message ?? t("longTxHint");
  const toastId = opts?.toastId ?? DEFAULT_TOAST_ID;

  useEffect(() => {
    if (!isPending) return;

    const timer = setTimeout(() => {
      toast.info(message, { id: toastId, duration: Infinity });
    }, thresholdMs);

    return () => {
      clearTimeout(timer);
      // Dismiss any hint toast emitted by this pending cycle. Safe if none fired.
      toast.dismiss(toastId);
    };
  }, [isPending, thresholdMs, message, toastId]);
}
